import { useEffect } from 'react';
import { useApiContext } from 'react-easyrocket';
import { useSetLanguageToUrl } from './useSetLanguageToUrl';
import { useTranslation } from 'react-i18next';
import { SupportedLanguages } from '../types';
import { COUNTRY_TO_LANG } from '../config';
import { useGetCountryConfig } from 'src/hooks/useGetCountryConfig';

/**
 * Хук, который вызывается ОДИН РАЗ на старте приложения и отвечает за определение языка пользователя
 * и настройку языка приложения.
 * Он синхронизирует язык приложения с языком пользователя, настраивает языковой префикс в URL,
 * и обновляет состояние приложения в соответствии с аутентификацией пользователя.
 * Хук обязательно должен располагаться в path={LANG_PARAM}
 */

export const useInitLanguageUrl = () => {
  const { isAuthenticated } = useApiContext();

  const setLanguageToUrl = useSetLanguageToUrl();

  const {
    i18n: { changeLanguage, language }
  } = useTranslation();

  const { userLanguage, isFetchingUserCountry, userCountry, fullInfo, isGBR, isLoadingFullInfo } =
    useGetCountryConfig({
      language: language as SupportedLanguages
    });

  const handleInitialStateChange = (lang: SupportedLanguages): void => {
    setLanguageToUrl(lang);
  };

  // Устанавливаем язык и конфигурацию
  useEffect(() => {
    if (!isFetchingUserCountry && !isAuthenticated) {
      changeLanguage(userLanguage).then(() => handleInitialStateChange(userLanguage));
    }
  }, [isFetchingUserCountry, userLanguage]);

  useEffect(() => {
    if (fullInfo?.language && userCountry?.country) {
      if (fullInfo?.language === COUNTRY_TO_LANG.GB && !isGBR) {
        changeLanguage(COUNTRY_TO_LANG.EN).then(() => {
          handleInitialStateChange(COUNTRY_TO_LANG.EN);
        });
        return;
      }

      const userCountryLanguage =
        isGBR && fullInfo?.language === COUNTRY_TO_LANG.EN
          ? COUNTRY_TO_LANG.GB
          : (language as SupportedLanguages);

      changeLanguage(userCountryLanguage).then(() => {
        handleInitialStateChange(userCountryLanguage);
      });
    }
  }, [isLoadingFullInfo, userCountry?.country]);

  useEffect(() => {
    if (!isAuthenticated && language === COUNTRY_TO_LANG.GB && isGBR) {
      changeLanguage(COUNTRY_TO_LANG.EN).then(() => handleInitialStateChange(COUNTRY_TO_LANG.EN));
      return;
    }
  }, [isAuthenticated]);
};
